import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SiteContext, Viewer } from '@shared/types';
import { setContributesPicks } from '@shared/nepali/browser/contribute';
import { api, ApiClientError } from './api';

/**
 * App-wide session + site context.
 *
 * `site` answers "whose site is this?" — it comes from the Host header, so the
 * same bundle renders the platform on kalampress.com and a single author's site on
 * poetrybyram.com. `viewer` is the logged-in writer, if any.
 */

interface SessionState {
  viewer: Viewer | null;
  site: SiteContext | null;
  loading: boolean;
  setViewer: (viewer: Viewer | null) => void;
  signIn: (email: string, password: string) => Promise<Viewer>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [site, setSite] = useState<SiteContext | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (signal?: AbortSignal) => {
    // The site context is required to render anything; the session is optional,
    // so a 401 here is an expected outcome rather than an error.
    const [siteResult, viewerResult] = await Promise.allSettled([api.context(signal), api.me()]);

    if (siteResult.status === 'fulfilled') setSite(siteResult.value);

    const user = viewerResult.status === 'fulfilled' ? viewerResult.value.user : null;
    setViewer(user);

    /*
     * The account's contribution setting reaches the browser that is doing the
     * typing. The transliterator's queue is local — it has to be, since it runs
     * for signed-out readers too — so a writer who turned contribution off on
     * their laptop would otherwise still be contributing from their phone.
     */
    if (user) setContributesPicks(user.contributesPicks);

    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { user } = await api.login({ email, password });
    setViewer(user);
    return user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch (error) {
      // A failed logout still clears local state — the cookie is httpOnly and
      // will be rejected on the next request anyway.
      if (!(error instanceof ApiClientError)) throw error;
    }
    setViewer(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({ viewer, site, loading, setViewer, signIn, signOut, refresh: () => load() }),
    [viewer, site, loading, signIn, signOut, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}

/** True when this hostname belongs to a single author rather than the platform. */
export function useTenant() {
  const { site } = useSession();
  return site?.tenant ?? null;
}
